'use client';

import { useRouter } from '@medizzconnect/i18n';
import { useState, useCallback, useEffect, useRef } from 'react';

interface BlogSearchProps {
  placeholder: string;
  category?: string;
  initialQuery?: string;
}

export function BlogSearch({ placeholder, category, initialQuery = '' }: BlogSearchProps) {
  const router = useRouter();
  const [query, setQuery] = useState(initialQuery);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);

  // URL mit Suchparametern aufbauen
  const buildUrl = useCallback(
    (q: string) => {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (q.trim()) params.set('q', q.trim());
      const qs = params.toString();
      return `/blog${qs ? `?${qs}` : ''}`;
    },
    [category]
  );

  // Debounced Live-Suche bei Tippen (300ms)
  useEffect(() => {
    // Beim ersten Render nicht navigieren
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      router.push(buildUrl(query));
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, router, buildUrl]);

  // Sofortige Suche bei Enter
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      router.push(buildUrl(query));
    },
    [router, buildUrl, query]
  );

  // Suchfeld leeren
  const handleClear = useCallback(() => {
    setQuery('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    router.push(buildUrl(''));
  }, [router, buildUrl]);

  return (
    <form onSubmit={handleSubmit} className="relative w-full max-w-md">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 pr-20 text-sm text-gray-700 shadow-sm placeholder:text-gray-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
      />
      {/* Clear-Button */}
      {query && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-10 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Suche leeren"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      {/* Such-Button */}
      <button
        type="submit"
        className="absolute right-1 top-1/2 -translate-y-1/2 rounded-md bg-teal-600 p-1.5 text-white hover:bg-teal-700 transition-colors"
        aria-label="Search"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </button>
    </form>
  );
}
